import * as React from "react"

import { cn } from "@/lib/utils"

// Holly decoration component
const HollyDecoration = ({ position }: { position: "top-left" | "top-right" | "bottom-left" | "bottom-right" }) => {
  const positionClasses = {
    "top-left": "top-1 left-1",
    "top-right": "top-1 right-1 -scale-x-100",
    "bottom-left": "bottom-1 left-1 -scale-y-100",
    "bottom-right": "bottom-1 right-1 scale-x-[-1] scale-y-[-1]",
  };

  return (
    <div className={cn("absolute pointer-events-none opacity-60", positionClasses[position])}>
      <svg width="24" height="20" viewBox="0 0 24 20" fill="none">
        {/* Holly leaves */}
        <path d="M8 10C6 8 3 9 2 11C1 13 2 15 4 15C6 15 8 13 8 10Z" fill="#228B22" />
        <path d="M12 8C10 6 7 7 6 9C5 11 6 13 8 13C10 13 12 11 12 8Z" fill="#2E8B2E" />
        {/* Holly berries */}
        <circle cx="9" cy="12" r="2.5" fill="#DC143C" />
        <circle cx="12" cy="10" r="2" fill="#B22222" />
        <circle cx="7" cy="10" r="1.5" fill="#DC143C" />
      </svg>
    </div>
  );
};

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  festive?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, festive = false, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "rounded-lg border bg-card text-card-foreground shadow-sm relative overflow-hidden",
        className
      )}
      {...props}
    >
      {festive && (
        <>
          <HollyDecoration position="top-right" />
          <HollyDecoration position="bottom-left" />
        </>
      )}
      {children}
    </div>
  )
)
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-2xl font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
